<template>
  <v-card
    class="d-flex flex-column align-center text-center error-page pa-3"
    :color="transparentBackground ? 'rgba(0,0,0,0)' : undefined"
    :flat="transparentBackground"
  >
    <div class="container">
      <v-img ref="illustration" :src="errorImageSource" max-height="300" />
      <div ref="headline" class="text-h3 mt-10">
        {{ errorHeadline }}
      </div>
      <div class="mt-3 mb-6">
        <span v-if="error.message || error.code" ref="errorMessage" class="d-block">
          {{ errorMessageText }}
        </span>
        <div v-if="error.errorId" class="d-flex align-center ga-1">
          <span>{{ $t('errorId') + ': ' + error.errorId }}</span>
          <sf-icon-button
            :size="20"
            :disabled="!isSupported"
            :icon="mdiContentCopy"
            @click="copy(error.errorId)"
          />
        </div>
        <span
          v-if="isQuotesPortalOrGuest && (error.code == 403 || error.code == 404)"
          ref="sorryMessage"
          class="d-block"
        >
          {{ $t('errorPageQuoteNotAvailableSorry') }}
        </span>
      </div>
      <sf-button
        v-if="error.code !== 403"
        ref="goStartButton"
        :to="{ name: 'StartPage' }"
        class="flex-grow-0 mb-1"
        block
        size="large"
      >
        {{ $t('errorPageStart') }}
      </sf-button>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import SfButton from '@/components/button/SfButton.vue'
import SfIconButton from '@/components/button/SfIconButton.vue'
import { isQuotesPortalOrGuest, useTitle } from '@/helpers'
import { getErrorHeadline, getErrorImageSource, getErrorMessage } from '@/modules/error/helpers'
import { StorefrontError } from '@/modules/error/types'
import { mdiContentCopy } from '@mdi/js'
import { useClipboard } from '@vueuse/core'
import { computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  error: {
    type: Object as PropType<StorefrontError>,
    required: true
  },
  transparentBackground: {
    type: Boolean,
    default: false
  }
})

const { copy, isSupported } = useClipboard()
const { t } = useI18n()
const pageTitle = computed(() =>
  props.error.code == 404 || props.error.code == 403
    ? `${props.error.message}`
    : t('errorPageHeadline')
)
useTitle(pageTitle)
const errorImageSource = getErrorImageSource(props.error.code!)
const errorHeadline = getErrorHeadline(props.error.code!)
const errorMessageText = getErrorMessage(props.error.code!, props.error.message!)
</script>

<style lang="scss" scoped>
.error-page {
  .container {
    max-width: 500px;
  }
}
</style>
